/**
 * Pure math utilities for nnInteractive coordinate conversions.
 *
 * Extracted from RegionGrowTool.ts so they can be unit-tested directly
 * without mocking Cornerstone viewport objects.
 */

type Pt3 = [number, number, number];

/**
 * Solve world→voxel conversion using Cramer's rule for a 3×3 affine system.
 *
 * Given origin, direction cosines (row/col/slice), and pixel spacing,
 * computes the [slice, row, col] voxel indices for a world-space point.
 *
 * @returns [z, y, x] integer voxel coordinates, or null if the matrix is singular.
 */
export function worldToVoxelZYXCramer(
  worldPoint: Pt3,
  origin: Pt3,
  rowCos: Pt3,
  colCos: Pt3,
  sliceDir: Pt3,
  colSp: number,
  rowSp: number,
  sliceSp: number,
): Pt3 | null {
  // Build columns of M: world_offset = M * [col, row, slice]
  const m00 = rowCos[0] * colSp,
    m01 = colCos[0] * rowSp,
    m02 = sliceDir[0] * sliceSp;
  const m10 = rowCos[1] * colSp,
    m11 = colCos[1] * rowSp,
    m12 = sliceDir[1] * sliceSp;
  const m20 = rowCos[2] * colSp,
    m21 = colCos[2] * rowSp,
    m22 = sliceDir[2] * sliceSp;

  // Cramer's rule (3x3)
  const det =
    m00 * (m11 * m22 - m12 * m21) - m01 * (m10 * m22 - m12 * m20) + m02 * (m10 * m21 - m11 * m20);
  if (Math.abs(det) < 1e-10) return null;

  const dx = worldPoint[0] - origin[0];
  const dy = worldPoint[1] - origin[1];
  const dz = worldPoint[2] - origin[2];

  const col =
    (dx * (m11 * m22 - m12 * m21) - m01 * (dy * m22 - m12 * dz) + m02 * (dy * m21 - m11 * dz)) /
    det;
  const row =
    (m00 * (dy * m22 - m12 * dz) - dx * (m10 * m22 - m12 * m20) + m02 * (m10 * dz - dy * m20)) /
    det;
  const slc =
    (m00 * (m11 * dz - dy * m21) - m01 * (m10 * dz - dy * m20) + dx * (m10 * m21 - m11 * m20)) /
    det;

  return [Math.round(slc), Math.round(row), Math.round(col)];
}

/**
 * Convert nnInteractive response contour points for a single slice into
 * world-space coordinates using a vtkImageData-like indexToWorld transform.
 *
 * @param contourPoints Array of [y, x] float pairs from the backend (cv2.findContours output)
 * @param sliceIndex    Axial slice index (the key from response.contours)
 * @param indexToWorld  Function mapping [col, row, slice] → [wx, wy, wz]
 * @returns Array of [wx, wy, wz] world coordinates
 */
export function nnContourToWorld(
  contourPoints: number[][],
  sliceIndex: number,
  indexToWorld: (ijk: [number, number, number]) => [number, number, number],
): Pt3[] {
  return contourPoints.map(([y, x]) => {
    const wp = indexToWorld([x, y, sliceIndex]);
    return [wp[0], wp[1], wp[2]] as Pt3;
  });
}

/**
 * Reorder vtkImageData [col, row, slice] spacing to [z, y, x] order
 * used by the nnInteractive backend.
 */
export function spacingToZYX(colRowSlice: [number, number, number]): Pt3 {
  return [colRowSlice[2], colRowSlice[1], colRowSlice[0]];
}
